import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth';
import { useI18n } from '../i18n/I18nContext';
import { AppHeader } from '../components/AppHeader';
import { useSmartBack } from '../hooks/useSmartBack';
import { setCoinBalance } from '../components/CoinChip';
import './TournamentsPage.css';

const API = import.meta.env.VITE_API_URL || 'http://localhost:8000';

interface Tournament {
  id: number;
  name: string;
  description: string | null;
  type: string;
  status: string;
  format: string;
  difficulty: number;
  max_participants: number;
  entry_fee_coins: number;
  prize_pool_coins: number;
  premium_only: boolean;
  starts_at: string;
  ends_at: string;
  registration_starts_at: string;
  participant_count: number;
  is_joined: boolean;
}

interface LeaderboardRow {
  rank: number;
  user_id: string;
  nickname: string;
  score: number;
  elapsed_seconds: number | null;
  mistakes: number;
  finished: boolean;
  reward_coins: number;
}

function formatCountdown(target: string, now: Date): string {
  const diffMs = new Date(target).getTime() - now.getTime();
  if (diffMs <= 0) return '00:00';
  const total = Math.floor(diffMs / 1000);
  const d = Math.floor(total / 86400);
  const h = Math.floor((total % 86400) / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (d > 0) return `${d}g ${h}sa`;
  if (h > 0) return `${h}sa ${m}d`;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export function TournamentsPage() {
  const { user, token } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const smartBack = useSmartBack('/');

  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [selected, setSelected] = useState<Tournament | null>(null);
  const [board, setBoard] = useState<LeaderboardRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(new Date());

  const headers = token ? { Authorization: `Bearer ${token}` } : undefined;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/tournaments`, { headers });
      if (res.ok) {
        const data = await res.json();
        setTournaments(data);
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadBoard = useCallback(async (id: number) => {
    try {
      const res = await fetch(`${API}/tournaments/${id}/leaderboard?limit=50`);
      if (res.ok) {
        const data = await res.json();
        setBoard(data.rows || []);
      }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (selected) loadBoard(selected.id);
  }, [selected, loadBoard]);

  const handleJoin = async (tournament: Tournament) => {
    if (!user || !token) {
      navigate('/auth');
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API}/tournaments/${tournament.id}/join`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.detail ?? t.tournaments.joinFailed);
        return;
      }
      if (typeof data.new_balance === 'number') {
        setCoinBalance(data.new_balance);
      }
      load();
    } finally {
      setBusy(false);
    }
  };

  const handlePlay = async (tournament: Tournament) => {
    if (!user || !token) {
      navigate('/auth');
      return;
    }
    navigate(`/tournament/${tournament.id}`);
  };

  const handleClaim = async (tournament: Tournament) => {
    if (!token) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/tournaments/${tournament.id}/claim`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.detail ?? t.tournaments.claimFailed);
        return;
      }
      if (typeof data.balance === 'number') setCoinBalance(data.balance);
      alert(t.tournaments.claimedToast.replace('{n}', String(data.reward_coins)));
      load();
    } finally {
      setBusy(false);
    }
  };

  const statusLabel = (s: string): string => {
    const map: Record<string, string> = {
      upcoming: t.tournaments.statusUpcoming,
      registration: t.tournaments.statusRegistration,
      running: t.tournaments.statusRunning,
      completed: t.tournaments.statusCompleted,
      cancelled: t.tournaments.statusCancelled,
    };
    return map[s] ?? s;
  };

  const typeLabel = (type: string): string => {
    const map: Record<string, string> = {
      free_weekly: t.tournaments.typeFreeWeekly,
      premium_only: t.tournaments.typePremiumOnly,
      coin_entry: t.tournaments.typeCoinEntry,
      special: t.tournaments.typeSpecial,
    };
    return map[type] ?? type;
  };

  const renderCountdown = (t1: Tournament): string => {
    if (t1.status === 'completed') return t.tournaments.ended;
    if (t1.status === 'running') return formatCountdown(t1.ends_at, now);
    return formatCountdown(t1.starts_at, now);
  };

  return (
    <div className="tournaments-page">
      <AppHeader onBack={smartBack} backLabel={t.common.back} />
      <div className="tournaments-container">
        <div className="tournaments-hero">
          <div className="tournaments-hero-icon">🏆</div>
          <h1>{t.tournaments.title}</h1>
          <p>{t.tournaments.subtitle}</p>
        </div>

        {loading ? (
          <div className="tournaments-loading">{t.common.loading}</div>
        ) : tournaments.length === 0 ? (
          <div className="tournaments-empty">
            <div className="tournaments-empty-icon">📅</div>
            <p>{t.tournaments.empty}</p>
          </div>
        ) : (
          <div className="tournaments-grid">
            {tournaments.map(tn => (
              <div key={tn.id} className={`tournament-card status-${tn.status}`}>
                <div className="tn-card-head">
                  <div className="tn-type">{typeLabel(tn.type)}</div>
                  <div className={`tn-status badge-${tn.status}`}>{statusLabel(tn.status)}</div>
                </div>
                <h2 className="tn-name">{tn.name}</h2>
                {tn.description && <p className="tn-desc">{tn.description}</p>}

                <div className="tn-meta">
                  <div className="tn-meta-row">
                    <span>{t.tournaments.difficulty}</span>
                    <strong>{t.difficulties[tn.difficulty] ?? tn.difficulty}</strong>
                  </div>
                  <div className="tn-meta-row">
                    <span>{t.tournaments.participants}</span>
                    <strong>{tn.participant_count}/{tn.max_participants}</strong>
                  </div>
                  <div className="tn-meta-row">
                    <span>{t.tournaments.entry}</span>
                    <strong>{tn.entry_fee_coins > 0 ? `${tn.entry_fee_coins} 🪙` : t.tournaments.entryFree}</strong>
                  </div>
                  <div className="tn-meta-row">
                    <span>{t.tournaments.prizePool}</span>
                    <strong className="tn-prize">{tn.prize_pool_coins.toLocaleString()} 🪙</strong>
                  </div>
                  <div className="tn-meta-row tn-countdown-row">
                    <span>{tn.status === 'running' ? t.tournaments.endsIn : t.tournaments.startsIn}</span>
                    <strong className="tn-countdown">{renderCountdown(tn)}</strong>
                  </div>
                </div>

                <div className="tn-actions">
                  {tn.status === 'completed' ? (
                    <>
                      <button className="btn-secondary" onClick={() => setSelected(tn)}>
                        {t.tournaments.viewResults}
                      </button>
                      {tn.is_joined && (
                        <button
                          className="btn-claim"
                          onClick={() => handleClaim(tn)}
                          disabled={busy}
                        >
                          {t.tournaments.claimReward}
                        </button>
                      )}
                    </>
                  ) : tn.is_joined ? (
                    tn.status === 'running' ? (
                      <button className="btn-primary" onClick={() => handlePlay(tn)}>
                        {t.tournaments.playNow}
                      </button>
                    ) : (
                      <button className="btn-secondary" disabled>
                        {t.tournaments.joined}
                      </button>
                    )
                  ) : (
                    <button
                      className="btn-primary"
                      onClick={() => handleJoin(tn)}
                      disabled={busy || tn.status === 'completed' || tn.status === 'upcoming'}
                    >
                      {tn.entry_fee_coins > 0
                        ? t.tournaments.joinForCoins.replace('{n}', String(tn.entry_fee_coins))
                        : t.tournaments.joinFree}
                    </button>
                  )}
                  <button className="btn-link" onClick={() => setSelected(tn)}>
                    {t.tournaments.leaderboard}
                  </button>
                </div>

                {tn.premium_only && <div className="tn-premium-badge">PRO</div>}
              </div>
            ))}
          </div>
        )}

        {selected && (
          <div className="tn-modal-overlay" onClick={() => setSelected(null)}>
            <div className="tn-modal" onClick={(e) => e.stopPropagation()}>
              <div className="tn-modal-head">
                <h3>{selected.name} — {t.tournaments.leaderboard}</h3>
                <button className="tn-modal-close" onClick={() => setSelected(null)}>×</button>
              </div>
              {board.length === 0 ? (
                <p className="tn-modal-empty">{t.tournaments.noEntries}</p>
              ) : (
                <table className="tn-board">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>{t.tournaments.player}</th>
                      <th>{t.tournaments.score}</th>
                      <th>{t.tournaments.time}</th>
                      <th>{t.tournaments.reward}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {board.map((row, idx) => (
                      <tr key={row.user_id} className={idx < 3 ? `top-${idx + 1}` : ''}>
                        <td>{row.rank || idx + 1}</td>
                        <td>{row.nickname}</td>
                        <td>{row.score}</td>
                        <td>{row.elapsed_seconds != null ? `${Math.floor(row.elapsed_seconds / 60)}:${String(row.elapsed_seconds % 60).padStart(2, '0')}` : '—'}</td>
                        <td>{row.reward_coins > 0 ? `${row.reward_coins} 🪙` : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
